import { useTranslation } from 'react-i18next';
import { categoryLabel } from '@/lib/category';
import type { ReactNode } from 'react';
import type { Event } from '@/types';
import { formatDateOnlyMonthDay, tryParseDateOnly } from '@/lib/date';
import { ExternalLink } from 'lucide-react';

interface EventCardProps {
  event: Event;
  showYear?: boolean;
}

/* Date column: small-caps month, serif day numeral, monospace year. */
function DateBlock({ month, day, year, showYear }: {
  month: number; day: number; year?: number; showYear?: boolean;
}) {
  const { t, i18n } = useTranslation('public');
  const monthLabel = new Intl.DateTimeFormat(i18n.resolvedLanguage, { month: 'short' }).format(new Date(2000, month, 1));
  let yearLabel: ReactNode = null;
  if (showYear && year) {
    yearLabel = <span className="mt-1 font-mono text-[11px] text-ink-2">{t('year', { year })}</span>;
  }
  return (
    <div className="flex flex-col items-start leading-none">
      <span className="kicker">{monthLabel}</span>
      <span
        className="mt-1 font-serif text-[1.75rem] font-medium tabular-nums"
        style={{ fontVariationSettings: '"opsz" 72' }}
      >
        {day}
      </span>
      {yearLabel}
    </div>
  );
}

function Title({ title, url }: { title: string; url?: string }) {
  if (!url) {
    return <span className="break-words">{title}</span>;
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="break-words transition-colors hover:text-accent-text"
      onClick={(e) => e.stopPropagation()}
    >
      {title}
      <ExternalLink className="ml-1.5 inline h-3.5 w-3.5 align-[-0.1em] text-ink-2" aria-hidden="true" />
    </a>
  );
}

/* One event as a hairline list row. Dated events (airshows and similar) carry a
   2px international-orange rule on the left and their date range in orange monospace. */
export function EventCard({ event, showYear = true }: EventCardProps) {
  useTranslation('public');
  // 如果没有有效日期则不渲染
  if (event.event_type === 'specific' ? !event.start_date : (!event.month || !event.day)) {
    return null;
  }
  const isSpecific = event.event_type === 'specific' && event.start_date;
  const start = isSpecific ? tryParseDateOnly(event.start_date!) : null;
  const end = isSpecific && event.end_date ? tryParseDateOnly(event.end_date) : null;
  if (isSpecific && !start) {
    return null;
  }

  // 日期信息
  const month = isSpecific ? start!.getMonth() : (event.month || 1) - 1;
  const day = isSpecific ? start!.getDate() : (event.day || 1);
  const year = event.year;

  // 特定日期事件显示完整日期范围；年度事件在日期栏显示原始年份。
  let dateRange: ReactNode = null;
  if (isSpecific) {
    let text = formatDateOnlyMonthDay(event.start_date!);
    if (end) {
      text = `${text} – ${formatDateOnlyMonthDay(event.end_date!)}`;
    }
    dateRange = <p className="m-0 mt-1 font-mono text-xs text-accent-text">{text}</p>;
  }

  let rowClassName = 'grid grid-cols-[3.5rem_minmax(0,1fr)] gap-4 border-b border-rule py-4 last:border-b-0';
  if (isSpecific) {
    rowClassName = `${rowClassName} border-l-2 border-l-accent pl-3`;
  }

  return (
    <article className={rowClassName}>
      <DateBlock month={month} day={day} year={year ?? undefined} showYear={showYear} />
      <div className="min-w-0">
        <h3 className="m-0 text-lg leading-snug md:text-xl">
          <Title title={event.title} url={event.url ?? undefined} />
        </h3>
        {dateRange}
        {event.description && (
          <p className="m-0 mt-1.5 line-clamp-2 text-[13px] leading-relaxed text-ink-2">{event.description}</p>
        )}
        {event.category && (
          <p className="kicker m-0 mt-2">{categoryLabel(event)}</p>
        )}
      </div>
    </article>
  );
}
